/**
 * Suppression de couleurs — même modèle que l'onglet « Trame DTF » de la PWA
 * HectoFlex : le client pipette des couleurs dans l'image, chaque couleur
 * choisie devient transparente PARTOUT (distance RGB ≤ tolérance globale).
 *
 * Non destructif : l'image d'origine est conservée telle quelle et le rendu
 * est recalculé à chaque changement (ajout/retrait d'une couleur, tolérance).
 * Retirer une couleur de la liste la fait donc réapparaître — c'est le
 * « retour en arrière » naturel de l'outil.
 */

import { autoSpeckleArea, defringeEdges, despeckle } from './despeckle';

// Cap de résolution de travail (vieux Safari plafonne les canvas ~16,7 Mpx)
const MAX_WORK_PX = 12e6;

export interface KeyedColor {
	r: number;
	g: number;
	b: number;
	/** point cliqué (coords image) : présent = la couleur n'est effacée que dans la zone reliée à ce point */
	seed?: { x: number; y: number };
	/** point cliqué, toujours mémorisé : permet de rebasculer la couleur en « zone » après coup */
	at: { x: number; y: number };
}

/** « partout » = la couleur saute dans toute l'image ; « zone » = seulement la zone reliée au clic (Marco 09.10.2026) */
export type BgMode = 'partout' | 'zone';

export interface BgSession {
	w: number;
	h: number;
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D;
	data: ImageData;
	orig: Uint8ClampedArray;
	keyed: KeyedColor[];
	tol: number;
	dirty: boolean;
	mode: BgMode;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('image'));
		img.src = src;
	});

/** Ouvre une session d'édition sur le canvas fourni (celui affiché dans la modale). */
export async function openBgSession(src: string, canvas: HTMLCanvasElement): Promise<BgSession> {
	const img = await loadImage(src);
	let w = img.naturalWidth || 1;
	let h = img.naturalHeight || 1;
	const scale = Math.min(1, Math.sqrt(MAX_WORK_PX / (w * h)));
	w = Math.max(1, Math.round(w * scale));
	h = Math.max(1, Math.round(h * scale));
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext('2d', { willReadFrequently: true });
	if (!ctx) throw new Error('canvas');
	ctx.clearRect(0, 0, w, h);
	ctx.drawImage(img, 0, 0, w, h);
	const data = ctx.getImageData(0, 0, w, h);
	const orig = new Uint8ClampedArray(data.data);
	return { w, h, canvas, ctx, data, orig, keyed: [], tol: 18, dirty: false, mode: 'partout' };
}

/** Repeint le canvas affiché depuis les données de travail. */
export function drawSession(sess: BgSession): void {
	sess.ctx.putImageData(sess.data, 0, 0);
}

/**
 * Recalcule le rendu depuis l'original : chaque pixel proche (distance RGB)
 * d'une des couleurs choisies passe en alpha 0. Même seuil que la PWA :
 * tolérance 0..80 → tol2 = (tol × 2.55)² × 3.
 */
export function recomputeSession(sess: BgSession): void {
	const d = sess.data.data;
	const o = sess.orig;
	d.set(o);
	const keyed = sess.keyed;
	if (keyed.length) {
		const t = sess.tol * 2.55;
		const tol2 = t * t * 3;
		const global = keyed.filter(k => !k.seed);
		if (global.length) {
			for (let p = 0; p < d.length; p += 4) {
				if (d[p + 3] === 0) continue;
				for (let k = 0; k < global.length; k++) {
					const kc = global[k];
					const dr = d[p] - kc.r;
					const dg = d[p + 1] - kc.g;
					const db = d[p + 2] - kc.b;
					if (dr * dr + dg * dg + db * db <= tol2) {
						d[p + 3] = 0;
						break;
					}
				}
			}
		}
		// zones reliées : remplissage de proche en proche depuis le point cliqué, sur l'image d'ORIGINE
		keyed.forEach(kc => {
			if (kc.seed) floodClear(d, o, sess.w, sess.h, kc.seed.x, kc.seed.y, kc, tol2);
		});
	}
	sess.dirty = keyed.length > 0;
	drawSession(sess);
}

/**
 * Baguette magique contiguë : efface (alpha 0 dans `d`) la zone 4-connexe de pixels proches de `col`
 * (distance RGB ≤ tol2, sur l'original `o`) reliée à (sx, sy). Remplissage par lignes (pile courte,
 * tableau « vu » d'un octet par pixel) : tient sur une image de 12 Mpx sans exploser la mémoire.
 */
function floodClear(d: Uint8ClampedArray, o: Uint8ClampedArray, w: number, h: number, sx: number, sy: number, col: KeyedColor, tol2: number): void {
	const match = (x: number, y: number): boolean => {
		const i = (y * w + x) * 4;
		if (o[i + 3] < 8) return false;
		const dr = o[i] - col.r;
		const dg = o[i + 1] - col.g;
		const db = o[i + 2] - col.b;
		return dr * dr + dg * dg + db * db <= tol2;
	};
	if (sx < 0 || sy < 0 || sx >= w || sy >= h || !match(sx, sy)) return;
	const seen = new Uint8Array(w * h);
	const stack: number[] = [sx, sy];
	while (stack.length) {
		const y = stack.pop() as number;
		let x = stack.pop() as number;
		if (seen[y * w + x]) continue;
		// remonter au début du segment
		while (x > 0 && !seen[y * w + x - 1] && match(x - 1, y)) x--;
		let above = false;
		let below = false;
		while (x < w && !seen[y * w + x] && match(x, y)) {
			const idx = y * w + x;
			seen[idx] = 1;
			d[idx * 4 + 3] = 0;
			if (y > 0) {
				const m = !seen[idx - w] && match(x, y - 1);
				if (m && !above) { stack.push(x, y - 1); above = true; } else if (!m) above = false;
			}
			if (y < h - 1) {
				const m = !seen[idx + w] && match(x, y + 1);
				if (m && !below) { stack.push(x, y + 1); below = true; } else if (!m) below = false;
			}
			x++;
		}
	}
}

/**
 * Pipette : lit la couleur d'ORIGINE au pixel cliqué et l'ajoute à la liste
 * des couleurs à retirer. Retourne null si la zone est déjà transparente.
 */
export function pickColor(sess: BgSession, x: number, y: number): KeyedColor | null {
	const px = Math.min(sess.w - 1, Math.max(0, Math.round(x)));
	const py = Math.min(sess.h - 1, Math.max(0, Math.round(y)));
	const i = (py * sess.w + px) * 4;
	if (sess.orig[i + 3] < 8) return null;
	const col: KeyedColor = { r: sess.orig[i], g: sess.orig[i + 1], b: sess.orig[i + 2], at: { x: px, y: py } };
	if (sess.mode === 'zone') col.seed = col.at;
	sess.keyed.push(col);
	recomputeSession(sess);
	return col;
}

/** Retire une couleur de la liste (elle réapparaît dans l'image). */
export function removeKeyedColor(sess: BgSession, index: number): void {
	sess.keyed.splice(index, 1);
	recomputeSession(sess);
}

/** Change la tolérance globale (0..80) et recalcule. */
export function setTolerance(sess: BgSession, tol: number): void {
	sess.tol = tol;
	recomputeSession(sess);
}

/**
 * Change le mode pour les PROCHAINS clics (les couleurs déjà retirées gardent le leur). Daniel 09.10 (après essai du
 * re-ciblage après coup, jugé confus) : dans l'assistant on choisit « Partout / Seulement la zone touchée » AVANT de toucher.
 */
export function setMode(sess: BgSession, mode: BgMode): void {
	sess.mode = mode;
}

/** Tout recommencer : vide la liste des couleurs. */
export function resetSession(sess: BgSession): void {
	sess.keyed = [];
	recomputeSession(sess);
}

/**
 * Nettoyage automatique (Daniel 09.10.2026, fonction d'Atlas) : après un retrait de couleur, on efface les petits
 * points qui traînent dans le fond, on rebouche les trous minuscules dans le dessin, puis on démêle les bords
 * (les pixels mi-encre mi-fond deviennent semi-transparents dans la couleur du dessin : plus d'escalier ni de halo,
 * sans rien éroder — un flou mangeait les traits de 1 à 2 px).
 * Ne fait rien tant qu'aucune couleur n'a été retirée. Retourne ce qui a été nettoyé (pour le message).
 */
export function cleanupSession(sess: BgSession): { islands: number; holes: number; edges: number } {
	if (!sess.keyed.length) return { islands: 0, holes: 0, edges: 0 };
	const d = sess.data.data;
	// un îlot n'est de la poussière que s'il a (presque) la couleur retirée : le point d'un « i » ou un petit détail du
	// logo, dans sa propre couleur, reste — même s'il est plus petit que le seuil
	const dustT = (Math.max(50, sess.tol + 24) * 2.55) ** 2 * 3;
	// grain de quelques pixels (≤ 3×3 sur une petite image, ≤ 10×10 sur 4000×4000) : effacé quelle que soit sa couleur
	const tiny = Math.max(10, Math.round(sess.w * sess.h * 0.000006));
	const r = despeckle(d, sess.w, sess.h, autoSpeckleArea(sess.w, sess.h), sess.orig, sess.keyed, dustT, tiny);
	const edges = defringeEdges(d, sess.orig, sess.w, sess.h, sess.keyed);
	drawSession(sess);
	return { islands: r.islands, holes: r.holes, edges };
}

/** Exporte le résultat : PNG rogné aux marges transparentes + nouvel aspect (nettoyage automatique inclus). */
export function exportSession(sess: BgSession): { src: string; aspect: number } {
	cleanupSession(sess);
	drawSession(sess);
	const { w, h } = sess;
	const d = sess.data.data;
	let minX = w;
	let minY = h;
	let maxX = -1;
	let maxY = -1;
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			if (d[(y * w + x) * 4 + 3] > 0) {
				if (x < minX) minX = x;
				if (x > maxX) maxX = x;
				if (y < minY) minY = y;
				maxY = y;
			}
		}
	}
	if (maxX < 0) {
		// tout a été effacé : on rend l'image telle quelle (le Valider est bloqué en amont)
		return { src: sess.canvas.toDataURL('image/png'), aspect: w / h };
	}
	const out = document.createElement('canvas');
	out.width = maxX - minX + 1;
	out.height = maxY - minY + 1;
	const octx = out.getContext('2d');
	if (!octx) return { src: sess.canvas.toDataURL('image/png'), aspect: w / h };
	octx.drawImage(sess.canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
	return { src: out.toDataURL('image/png'), aspect: out.width / out.height };
}

/** Reste-t-il au moins un pixel visible ? (garde-fou avant Valider) */
export function hasVisiblePixels(sess: BgSession): boolean {
	const d = sess.data.data;
	for (let p = 3; p < d.length; p += 4) if (d[p] > 0) return true;
	return false;
}

/**
 * L'image est-elle entièrement opaque ? (échantillon ≤256 px) — un JPEG l'est
 * toujours : c'est le signal « il y a sûrement un fond à enlever » à l'import.
 */
export async function isFullyOpaque(src: string): Promise<boolean> {
	const img = await loadImage(src);
	const scale = Math.min(1, 256 / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
	const w = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
	const h = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
	const c = document.createElement('canvas');
	c.width = w;
	c.height = h;
	const ctx = c.getContext('2d', { willReadFrequently: true });
	if (!ctx) return false;
	ctx.drawImage(img, 0, 0, w, h);
	const d = ctx.getImageData(0, 0, w, h).data;
	for (let p = 3; p < d.length; p += 4) if (d[p] < 250) return false;
	return true;
}
